import eventBus from './event-bus';

const activeToastsByKey = new Map();
const recentToastMessages = new Map();
let lastErrorLikeToastAt = 0;

export function markToastMessageShown(message, isErrorLike = false) {
    const now = Date.now();
    if (isErrorLike) {
        lastErrorLikeToastAt = now;
    }
    const clean = typeof message === 'string' ? message.trim() : '';
    if (clean) {
        recentToastMessages.set(clean, now);
    }
}

export function wasToastRecentlyShown(message, windowMs = 1500) {
    const now = Date.now();
    if (lastErrorLikeToastAt > 0 && (now - lastErrorLikeToastAt) < 250) {
        return true;
    }
    const clean = typeof message === 'string' ? message.trim() : '';
    if (!clean) return false;
    const prevAt = recentToastMessages.get(clean);
    return typeof prevAt === 'number' && (now - prevAt) < windowMs;
}

export const TOAST_HISTORY_STORAGE_KEY = 'vt_toast_history_v1';
export const TOAST_HISTORY_MAX_AGE_MS = 3 * 24 * 60 * 60 * 1000; // 3 days
export const TOAST_HISTORY_MAX_ITEMS = 100;

export function loadToastHistory() {
    try {
        if (typeof localStorage === 'undefined') return [];
        const raw = localStorage.getItem(TOAST_HISTORY_STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        const cutoff = Date.now() - TOAST_HISTORY_MAX_AGE_MS;
        const filtered = parsed.filter(item => item && typeof item.timestamp === 'number' && item.timestamp >= cutoff);
        if (filtered.length !== parsed.length) {
            localStorage.setItem(TOAST_HISTORY_STORAGE_KEY, JSON.stringify(filtered.slice(0, TOAST_HISTORY_MAX_ITEMS)));
        }
        return filtered.slice(0, TOAST_HISTORY_MAX_ITEMS);
    } catch (e) {
        return [];
    }
}

export function saveToastHistory(items) {
    try {
        if (typeof localStorage === 'undefined') return;
        const cutoff = Date.now() - TOAST_HISTORY_MAX_AGE_MS;
        const clean = (Array.isArray(items) ? items : [])
            .filter(item => item && typeof item.timestamp === 'number' && item.timestamp >= cutoff)
            .slice(0, TOAST_HISTORY_MAX_ITEMS);
        localStorage.setItem(TOAST_HISTORY_STORAGE_KEY, JSON.stringify(clean));
    } catch (e) {
        // Ignore storage quota errors
    }
}

export function recordToastHistoryEntry({ variant = 'info', title = '', message = '', meta = null }) {
    try {
        const cleanTitle = typeof title === 'string' ? title.trim() : (title ? String(title).trim() : '');
        const cleanMsg = typeof message === 'string' ? message.trim() : '';
        if (!cleanTitle && !cleanMsg) return;

        markToastMessageShown(cleanMsg, variant === 'danger' || variant === 'warning');
        const now = Date.now();
        const history = loadToastHistory();
        const top = history[0];
        if (top && top.variant === variant && cleanMsg && top.message === cleanMsg && top.title !== cleanTitle && (now - top.timestamp) < 2000) {
            if (cleanTitle) top.title = cleanTitle;
            top.timestamp = now;
            top.unread = true;
            saveToastHistory(history);
            eventBus.$emit('vt-toast-history-updated', history);
            return;
        }
        if (top && top.variant === variant && top.title === cleanTitle && top.message === cleanMsg && (now - top.timestamp) < 60 * 1000) {
            top.timestamp = now;
            top.repeatCount = (top.repeatCount || 1) + 1;
            top.unread = true;
            saveToastHistory(history);
            eventBus.$emit('vt-toast-history-updated', history);
            return;
        }

        const entry = {
            id: `th-${now}-${Math.random().toString(36).slice(2, 7)}`,
            variant: variant || 'info',
            title: cleanTitle || 'Notification',
            message: cleanMsg,
            timestamp: now,
            repeatCount: 1,
            unread: true,
            meta: meta || null
        };
        const updated = [entry, ...history].slice(0, TOAST_HISTORY_MAX_ITEMS);
        saveToastHistory(updated);
        eventBus.$emit('vt-toast-history-updated', updated);
    } catch (e) {
        // Ignore history logging errors
    }
}

export function clearToastHistoryStorage() {
    saveToastHistory([]);
    eventBus.$emit('vt-toast-history-updated', []);
}

export function markAllToastHistoryRead() {
    const history = loadToastHistory();
    let changed = false;
    history.forEach(item => {
        if (item.unread) {
            item.unread = false;
            changed = true;
        }
    });
    if (changed) {
        saveToastHistory(history);
        eventBus.$emit('vt-toast-history-updated', history);
    }
}

let toastSeq = 0;
const activeToastControllers = new Map();

function cleanupToastController(toastId) {
    if (!toastId || !activeToastControllers.has(toastId)) return;
    const ctrl = activeToastControllers.get(toastId);
    activeToastControllers.delete(toastId);
    if (ctrl.timerId) clearTimeout(ctrl.timerId);
    if (ctrl.pollIds && Array.isArray(ctrl.pollIds)) {
        ctrl.pollIds.forEach(id => clearTimeout(id));
    }
    if (typeof ctrl.detach === 'function') {
        try { ctrl.detach(); } catch (_) { /* ignore */ }
    }
}

function buildToastProgressNode(h, autoHideDelay, variant) {
    if (!autoHideDelay || autoHideDelay <= 0) return null;
    const v = variant || 'info';
    return h('div', {
        class: ['vt-toast-progress-track', `vt-toast-progress-${v}`],
        attrs: { 'data-test': 'toast-progress-track' }
    }, [
        h('div', {
            class: 'vt-toast-progress-bar',
            style: {
                animationDuration: `${autoHideDelay}ms`
            },
            attrs: { 'data-test': 'toast-progress-bar' }
        })
    ]);
}

function attachToastProgressController(bvToast, toastId, keys, autoHideDelay) {
    if (!bvToast || !toastId || !autoHideDelay || autoHideDelay <= 0) return;
    cleanupToastController(toastId);

    const nowMs = () => (typeof performance !== 'undefined' && typeof performance.now === 'function')
        ? performance.now()
        : Date.now();

    const keyList = Array.isArray(keys) ? keys.filter(Boolean) : (keys ? [keys] : []);
    const ctrl = {
        remainingMs: autoHideDelay,
        lastResumedAt: nowMs(),
        isPaused: false,
        isClosed: false,
        timerId: null,
        pollIds: [],
        detach: null
    };
    activeToastControllers.set(toastId, ctrl);

    const dismissToast = () => {
        if (ctrl.isClosed) return;
        ctrl.isClosed = true;
        cleanupToastController(toastId);
        keyList.forEach(k => {
            if (activeToastsByKey.get(k) === toastId) {
                activeToastsByKey.delete(k);
            }
        });
        try {
            bvToast.hide(toastId);
        } catch (_) {
            // Ignore if already unmounted
        }
    };

    const scheduleTimer = (ms) => {
        if (ctrl.timerId) clearTimeout(ctrl.timerId);
        ctrl.timerId = setTimeout(dismissToast, Math.max(16, ms));
    };

    scheduleTimer(ctrl.remainingMs);

    const bindDomListeners = () => {
        if (ctrl.isClosed || ctrl.detach || typeof document === 'undefined') return;
        const toastRoot = document.getElementById(`${toastId}__toast_outer`) || document.getElementById(toastId);
        if (!toastRoot) return;
        const barEl = toastRoot.querySelector('.vt-toast-progress-bar');

        const pauseProgress = () => {
            if (ctrl.isPaused || ctrl.isClosed) return;
            ctrl.isPaused = true;
            const elapsed = Math.max(0, nowMs() - ctrl.lastResumedAt);
            ctrl.remainingMs = Math.max(40, ctrl.remainingMs - elapsed);
            if (ctrl.timerId) {
                clearTimeout(ctrl.timerId);
                ctrl.timerId = null;
            }
            if (barEl) {
                barEl.style.animationPlayState = 'paused';
            }
            toastRoot.setAttribute('data-progress-paused', 'true');
        };

        const resumeProgress = () => {
            if (!ctrl.isPaused || ctrl.isClosed) return;
            ctrl.isPaused = false;
            ctrl.lastResumedAt = nowMs();
            if (barEl) {
                barEl.style.animationPlayState = 'running';
            }
            toastRoot.setAttribute('data-progress-paused', 'false');
            scheduleTimer(ctrl.remainingMs);
        };

        const onMouseEnter = () => pauseProgress();
        const onMouseLeave = () => resumeProgress();
        const onFocusIn = () => pauseProgress();
        const onFocusOut = (evt) => {
            if (evt && evt.relatedTarget && toastRoot.contains(evt.relatedTarget)) {
                return;
            }
            if (toastRoot.matches && toastRoot.matches(':hover')) {
                return;
            }
            resumeProgress();
        };
        const onAnimationEnd = (evt) => {
            if (evt && barEl && evt.target !== barEl) return;
            if (!ctrl.isPaused) {
                dismissToast();
            }
        };

        toastRoot.addEventListener('mouseenter', onMouseEnter);
        toastRoot.addEventListener('mouseleave', onMouseLeave);
        toastRoot.addEventListener('focusin', onFocusIn);
        toastRoot.addEventListener('focusout', onFocusOut);
        if (barEl) {
            barEl.addEventListener('animationend', onAnimationEnd);
        }

        ctrl.detach = () => {
            toastRoot.removeEventListener('mouseenter', onMouseEnter);
            toastRoot.removeEventListener('mouseleave', onMouseLeave);
            toastRoot.removeEventListener('focusin', onFocusIn);
            toastRoot.removeEventListener('focusout', onFocusOut);
            if (barEl) {
                barEl.removeEventListener('animationend', onAnimationEnd);
            }
        };

        if (toastRoot.matches && toastRoot.matches(':hover')) {
            pauseProgress();
        }
    };

    [0, 25, 65, 150].forEach(delay => {
        const pid = setTimeout(bindDomListeners, delay);
        ctrl.pollIds.push(pid);
    });
}

function closePreviousToast(bvToast, key) {
    if (!bvToast || !key) return;
    if (activeToastsByKey.has(key)) {
        const prevId = activeToastsByKey.get(key);
        cleanupToastController(prevId);
        try {
            bvToast.hide(prevId);
            setTimeout(() => {
                try {
                    bvToast.hide(prevId);
                    const delayedEl = document.getElementById(prevId);
                    if (delayedEl) delayedEl.remove();
                    const delayedOuter = document.getElementById(`${prevId}__toast_outer`);
                    if (delayedOuter) delayedOuter.remove();
                } catch (_) { /* ignore */ }
            }, 0);
            setTimeout(() => {
                try {
                    bvToast.hide(prevId);
                    const delayedEl2 = document.getElementById(prevId);
                    if (delayedEl2) delayedEl2.remove();
                    const delayedOuter2 = document.getElementById(`${prevId}__toast_outer`);
                    if (delayedOuter2) delayedOuter2.remove();
                } catch (_) { /* ignore */ }
            }, 45);
            const el = document.getElementById(prevId);
            if (el) {
                el.remove();
            }
        } catch (e) {
            // Ignore if already gone
        }
        activeToastsByKey.delete(key);
    }
}

function registerToast(key, id, autoHideDelay) {
    activeToastsByKey.set(key, id);
    if (autoHideDelay && autoHideDelay > 0) {
        setTimeout(() => {
            if (activeToastsByKey.get(key) === id && !activeToastControllers.has(id)) {
                activeToastsByKey.delete(key);
            }
        }, autoHideDelay + 500);
    }
}

function getToastKey(variant, title, message) {
    const v = variant || 'default';
    const t = typeof title === 'string' ? title.trim() : (title ? String(title) : '');
    const m = typeof message === 'string' ? message.trim() : (message ? String(message) : '');
    return `${v}::${t}::${m}`;
}

export default {
    methods: {
        toastSuccess(title, message = ' ', showSeconds = 1.5) {
            this.toastIt(title, message, 'success', showSeconds * 1000);
        },
        toastInfo(title, message = ' ', showSeconds = 3) {
            this.toastIt(title, message, 'info', showSeconds * 1000);
        },
        toastWarning(title, message = ' ', showSeconds = 3) {
            if (this.$store?.state?.isServiceUnavailable) return;
            this.toastIt(title, message, 'warning', showSeconds * 1000);
        },
        toastError(title, message = ' ', showSeconds = 5) {
            if (this.$store?.state?.isServiceUnavailable) return;
            if (title && (title.status === 402 || title.isPlanUpgrade)) return;
            if (message && (message.status === 402 || message.isPlanUpgrade)) return;
            if (title && (title.isNetworkError || title.status === 0)) return;
            if (message && (message.isNetworkError || message.status === 0)) return;

            const permPayload = (message && typeof message === 'object' && message.isPermissionError) ? message :
                                (title && typeof title === 'object' && title.isPermissionError) ? title : null;
            if (permPayload) {
                this.toastPermissionRequired(permPayload);
                return;
            }

            const strMsg = typeof message === 'string' ? message : (typeof title === 'string' ? title : '');
            if (strMsg.includes('Force push permission') || strMsg.includes('ForcePushRequired') || strMsg.includes('is required to delete branches')) {
                this.toastPermissionRequired({
                    actionName: 'Delete Branch',
                    requiredScope: 'Code (Full) / Repository Force Push Security',
                    requiredScopeKey: 'vso.code_full',
                    requiredPermission: 'Force push (rewrite history, delete branches and tags)',
                    explanation: strMsg,
                    guidance: "1. In Azure DevOps: Project Settings → Repositories → select your repository → Security → ensure 'Force push (rewrite history, delete branches and tags)' is set to Allow for your user/group.\n2. In Azure DevOps: User Settings (top right) → Personal Access Tokens → Edit token → Ensure 'Code (Read & Write)' or 'Code (Full)' is checked.",
                    externalUrl: 'https://dev.azure.com/_usersSettings/tokens',
                    externalLabel: 'Open ADO PAT Configuration'
                });
                return;
            }

            if (strMsg.includes('Contribute to pull requests') || (strMsg.includes('pull request') && (strMsg.includes('permission') || strMsg.includes('authorized') || strMsg.includes('Code (read & write)')))) {
                this.toastPermissionRequired({
                    actionName: 'Update Pull Request',
                    requiredScope: 'Code (read & write)',
                    requiredScopeKey: 'vso.code_write',
                    requiredPermission: 'Contribute to pull requests',
                    explanation: strMsg,
                    guidance: "In Azure DevOps: User Settings (top right) → Personal Access Tokens → Edit token → Ensure 'Code (read & write)' is checked."
                });
                return;
            }

            this.toastIt(title, message, 'danger', showSeconds * 1000);
        },
        toastPermissionRequired(details = {}, showSeconds = 0) {
            const h = this.$createElement;
            const key = 'ado-permission-toast';
            closePreviousToast(this.$bvToast, key);

            const toastId = `vt-perm-${++toastSeq}`;
            // Permission details toasts require reading & troubleshooting steps; they should never auto-close unless explicitly requested
            const noAutoHide = details.autoHide ? false : true;
            const permAutoHideMs = noAutoHide ? 0 : (showSeconds || 10) * 1000;
            registerToast(key, toastId, permAutoHideMs);

            const actionName = details.actionName || 'Requested Operation';
            const scope = details.requiredScope || 'Code (read & write)';
            const perm = details.requiredPermission || '';
            const explanation = details.explanation || details.message || 'Azure DevOps rejected this operation due to missing permissions or Personal Access Token (PAT) scopes.';
            const guidance = details.guidance || 'In Azure DevOps: User Settings (top right) → Personal Access Tokens → Edit token → Add required scope.';
            recordToastHistoryEntry({
                variant: 'danger',
                title: `Permission Required: ${actionName}`,
                message: `${explanation}${scope ? ` (PAT Scope: ${scope})` : ''}`,
                meta: { type: 'permission', scope, perm, guidance }
            });

            const vNodesMsg = h('div', {
                class: 'd-flex flex-column pt-1',
                attrs: { 'data-test': 'toast-danger', 'data-test-permission': 'true' }
            }, [
                h('div', { class: 'd-flex flex-column align-items-start mb-2' }, [
                    h('span', { class: 'badge badge-info mb-1 py-1 px-2 font-weight-bold shadow-sm' }, [
                        h('i', { class: 'bi bi-key-fill mr-1' }),
                        `PAT Scope: ${scope}`
                    ]),
                    perm ? h('span', { class: 'badge badge-warning py-1 px-2 font-weight-bold text-dark shadow-sm' }, [
                        h('i', { class: 'bi bi-shield-lock mr-1' }),
                        `Permission: ${perm}`
                    ]) : null
                ]),
                h('div', { class: 'small mb-2 font-weight-bold text-dark' }, explanation),
                h('div', { class: 'small rounded vt-permission-guidance' }, [
                    h('div', { class: 'font-weight-bold mb-1 d-flex align-items-center text-primary' }, [
                        h('i', { class: 'bi bi-info-circle-fill mr-1' }),
                        'How to fix in Azure DevOps:'
                    ]),
                    h('div', { class: 'vt-permission-guidance-text' }, guidance),
                    h('div', { class: 'd-flex align-items-center flex-wrap mt-2 pt-2 border-top' }, [
                        h('button', {
                            class: 'btn btn-xs btn-primary font-weight-bold mr-2 py-1 px-2',
                            attrs: { type: 'button', 'data-test': 'btn-open-config-permission-toast' },
                            on: {
                                click: () => {
                                    cleanupToastController(toastId);
                                    this.$bvToast.hide(toastId);
                                    if (this.$router && this.$route?.path !== '/config') {
                                        this.$router.push({ name: 'config' }).catch(() => {});
                                    }
                                }
                            }
                        }, [
                            h('i', { class: 'bi bi-gear-fill mr-1' }),
                            details.configLabel || 'Update Token in Config'
                        ]),
                        details.externalUrl ? h('a', {
                            class: 'btn btn-xs btn-outline-secondary font-weight-bold py-1 px-2',
                            attrs: {
                                href: details.externalUrl,
                                target: '_blank',
                                rel: 'noopener noreferrer',
                                'data-test': 'link-ado-branches-toast'
                            }
                        }, [
                            h('i', { class: 'bi bi-box-arrow-up-right mr-1' }),
                            details.externalLabel || 'Open ADO PAT Configuration'
                        ]) : null,
                        h('button', {
                            class: 'btn btn-xs btn-outline-secondary font-weight-bold py-1 px-2 ml-auto',
                            attrs: { type: 'button', 'data-test': 'btn-dismiss-permission-toast' },
                            on: {
                                click: () => {
                                    cleanupToastController(toastId);
                                    this.$bvToast.hide(toastId);
                                }
                            }
                        }, 'Dismiss')
                    ])
                ])
            ]);

            const permProgressNode = buildToastProgressNode(h, permAutoHideMs, 'danger');
            this.$bvToast.toast([vNodesMsg, permProgressNode].filter(Boolean), {
                id: toastId,
                title: `Permission Required: ${actionName}`,
                variant: 'danger',
                solid: true,
                noAutoHide: true,
                toaster: 'b-toaster-bottom-right',
                appendToast: false,
                toastClass: ['vt-toast', 'vt-toast-danger', 'vt-toast-permission']
            });
            if (permAutoHideMs > 0) {
                attachToastProgressController(this.$bvToast, toastId, [key], permAutoHideMs);
            }
        },
        toastSpecialThanks(type = 'feedback', customMessage = '', showSeconds = 6) {
            const h = this.$createElement;
            const key = 'special-thanks-toast';
            closePreviousToast(this.$bvToast, key);

            const toastId = `vt-thanks-${++toastSeq}`;
            const thanksAutoHideMs = showSeconds * 1000;
            registerToast(key, toastId, thanksAutoHideMs);

            const isProblem = type === 'problem';
            const thanksVariant = isProblem ? 'info' : 'success';
            const iconClass = isProblem ? 'bi bi-shield-fill-check text-success' : 'bi bi-stars text-warning';
            const headline = isProblem
                ? 'Thank you for reporting this issue!'
                : 'Thank you for your rating & feedback!';
            const defaultMsg = isProblem
                ? 'Your report and diagnostic telemetry help us pinpoint glitches and make VelocyTrack more reliable for everyone.'
                : 'Your feedback and insights mean the world to us. As an early adopter, your thoughts directly guide upcoming improvements!';
            const msgText = customMessage || defaultMsg;
            const badgeText = isProblem ? 'Bug Hunter' : 'Community Voice';
            recordToastHistoryEntry({
                variant: thanksVariant,
                title: isProblem ? 'Thank You for Helping Us Improve!' : 'Special Thanks from VelocyTrack!',
                message: msgText
            });

            const vNodesMsg = h('div', {
                class: 'd-flex align-items-start pt-1',
                attrs: { 'data-test': `toast-${thanksVariant}` }
            }, [
                h('div', { class: 'mr-3 h2 mb-0 d-flex align-items-center' }, [
                    h('i', { class: iconClass })
                ]),
                h('div', { class: 'flex-grow-1' }, [
                    h('div', { class: 'd-flex align-items-center justify-content-between mb-1' }, [
                        h('span', { class: 'font-weight-bold' }, headline),
                        h('span', { class: isProblem ? 'badge badge-info ml-2' : 'badge badge-success ml-2' }, badgeText)
                    ]),
                    h('div', { class: 'small text-muted leading-relaxed' }, msgText)
                ])
            ]);

            const thanksProgressNode = buildToastProgressNode(h, thanksAutoHideMs, thanksVariant);
            this.$bvToast.toast([vNodesMsg, thanksProgressNode].filter(Boolean), {
                id: toastId,
                title: isProblem ? '🙏 Thank You for Helping Us Improve!' : '⭐ Special Thanks from VelocyTrack!',
                variant: thanksVariant,
                solid: true,
                noAutoHide: true,
                toaster: 'b-toaster-bottom-right',
                appendToast: false,
                toastClass: ['vt-toast', `vt-toast-${thanksVariant}`]
            });
            attachToastProgressController(this.$bvToast, toastId, [key], thanksAutoHideMs);
        },
        toastUpgradeRequired(reqPlan = 'Pro Plan', message = '', showSeconds = 8) {
            const h = this.$createElement;
            const key = 'plan-upgrade-toast';
            closePreviousToast(this.$bvToast, key);

            const toastId = `vt-upgrade-${++toastSeq}`;
            const upgradeAutoHideMs = showSeconds * 1000;
            registerToast(key, toastId, upgradeAutoHideMs);

            const displayPlan = (reqPlan === 'Solo' || reqPlan === 'Solo Pro') ? 'Pro Plan' : reqPlan;
            const tierPitch = displayPlan === 'Team'
                ? ' Unlock squad capacity, PTO conflict alerts and bottleneck radar.'
                : (displayPlan === 'Pro Plan' ? ' Unlock 1-click edits, hygiene fixes and unlimited sprint history.' : '');
            const msgText = message || `This feature requires the ${displayPlan} plan.${tierPitch}`;
            recordToastHistoryEntry({
                variant: 'warning',
                title: 'Plan Upgrade Required',
                message: msgText
            });
            const vNodesMsg = h('div', {
                class: 'd-flex flex-column pt-1',
                attrs: { 'data-test': 'toast-warning', 'data-test-plan-upgrade': 'true' }
            }, [
                h('div', { class: 'mb-2' }, msgText),
                h('button', {
                    class: 'btn btn-sm btn-warning font-weight-bold align-self-start text-dark shadow-sm',
                    style: 'cursor: pointer;',
                    attrs: { 'data-test': 'btn-upgrade-plan-toast' },
                    on: {
                        click: () => {
                            closePreviousToast(this.$bvToast, key);
                            if (this.$route.path !== '/plans') {
                                this.$router.push({
                                    path: '/plans',
                                    query: {
                                        reason: `Accessing a ${displayPlan}-tier feature`,
                                        requiredPlan: displayPlan
                                    }
                                }).catch(() => {});
                            }
                        }
                    }
                }, [
                    h('i', { class: 'bi bi-arrow-up-circle-fill mr-1' }),
                    'Upgrade Plan'
                ])
            ]);

            const upgradeProgressNode = buildToastProgressNode(h, upgradeAutoHideMs, 'warning');
            this.$bvToast.toast([vNodesMsg, upgradeProgressNode].filter(Boolean), {
                id: toastId,
                title: 'Plan Upgrade Required',
                variant: 'warning',
                solid: true,
                noAutoHide: true,
                toaster: 'b-toaster-bottom-right',
                appendToast: false,
                toastClass: ['vt-toast', 'vt-toast-warning']
            });
            attachToastProgressController(this.$bvToast, toastId, [key], upgradeAutoHideMs);
        },
        toastRuleValidation(fieldName, customMessage, onAddColumn, showSeconds = 10) {
            const h = this.$createElement;
            const key = 'rule-validation-toast';
            closePreviousToast(this.$bvToast, key);

            const toastId = `vt-rule-${++toastSeq}`;
            const ruleAutoHideMs = showSeconds * 1000;
            registerToast(key, toastId, ruleAutoHideMs);

            const displayField = fieldName ? `'${fieldName}'` : 'a required field';
            const msgText = customMessage || `Azure DevOps rule requires a value for ${displayField}. Add this column to your grid in VelocyTrack to provide a value.`;
            recordToastHistoryEntry({
                variant: 'danger',
                title: 'Azure DevOps Rule Required',
                message: msgText
            });

            const vNodesMsg = h('div', {
                class: 'd-flex flex-column pt-1',
                attrs: { 'data-test': 'toast-danger', 'data-test-rule-validation': 'true' }
            }, [
                h('div', { class: 'mb-2' }, msgText),
                onAddColumn ? h('button', {
                    class: 'btn btn-sm btn-light font-weight-bold align-self-start text-danger shadow-sm border',
                    style: 'cursor: pointer;',
                    attrs: { 'data-test': 'btn-open-column-designer-toast' },
                    on: {
                        click: () => {
                            closePreviousToast(this.$bvToast, key);
                            onAddColumn();
                        }
                    }
                }, [
                    h('i', { class: 'bi bi-gear-fill mr-1' }),
                    'Open Column Designer'
                ]) : null
            ]);

            const ruleProgressNode = buildToastProgressNode(h, ruleAutoHideMs, 'danger');
            this.$bvToast.toast([vNodesMsg, ruleProgressNode].filter(Boolean), {
                id: toastId,
                title: 'Azure DevOps Rule Required',
                variant: 'danger',
                solid: true,
                noAutoHide: true,
                toaster: 'b-toaster-bottom-right',
                appendToast: false,
                toastClass: ['vt-toast', 'vt-toast-danger']
            });
            attachToastProgressController(this.$bvToast, toastId, [key], ruleAutoHideMs);
        },
        toastIt(title, message, variant, autoHideDelay) {
            const key = getToastKey(variant, title, message);
            const cleanBody = typeof message === 'string' ? message.trim() : '';
            const msgKey = cleanBody ? `${variant || 'default'}::MSG::${cleanBody}` : null;
            if (msgKey) closePreviousToast(this.$bvToast, msgKey);
            closePreviousToast(this.$bvToast, key);

            const toastId = `vt-toast-${++toastSeq}`;
            registerToast(key, toastId, autoHideDelay);
            if (msgKey) registerToast(msgKey, toastId, autoHideDelay);

            const h = this.$createElement;
            const hasBodyText = typeof message === 'string' && message.trim().length > 0;
            const contentNode = typeof message === 'string'
                ? h('div', {
                    class: hasBodyText ? 'vt-toast-msg' : 'vt-toast-msg-empty',
                    attrs: { 'data-test': `toast-${variant}` }
                }, hasBodyText ? message : title)
                : message;

            const toastClasses = ['vt-toast', `vt-toast-${variant}`];
            if (!hasBodyText && typeof message === 'string') {
                toastClasses.push('vt-toast-title-only');
            }

            recordToastHistoryEntry({
                variant: variant || 'info',
                title: typeof title === 'string' ? title : '',
                message: hasBodyText ? message : ''
            });
            const progressNode = buildToastProgressNode(h, autoHideDelay, variant);
            this.$bvToast.toast([contentNode, progressNode].filter(Boolean), {
                id: toastId,
                title: title,
                variant: variant,
                solid: true,
                noAutoHide: Boolean(autoHideDelay && autoHideDelay > 0),
                autoHideDelay: autoHideDelay,
                toaster: 'b-toaster-bottom-right',
                toastClass: toastClasses
            });
            if (autoHideDelay && autoHideDelay > 0) {
                attachToastProgressController(this.$bvToast, toastId, msgKey ? [key, msgKey] : [key], autoHideDelay);
            }
        }
    }
}
